/**
 * Settings Page Component
 * Manages email alerts, EMQ threshold, alert preferences, and META settings
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Settings.css';

const Settings = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('alert-preferences');
  const [expandedPreference, setExpandedPreference] = useState(null);
  const [alertPreferences, setAlertPreferences] = useState([
    {
      key: 'web-emq-threshold',
      label: 'Web EMQ Threshold',
      description: 'Trigger alerts when the average web EMQ score drops below this value.',
      value: 7.5,
      enabled: true,
      unit: '',
      min: 0,
      max: 10,
      step: 0.1,
    },
    {
      key: 'offline-emq-threshold',
      label: 'Offline EMQ Threshold',
      description: 'Notify when the offline EMQ score crosses the configured boundary.',
      value: 6.8,
      enabled: true,
      unit: '',
      min: 0,
      max: 10,
      step: 0.1,
    },
    {
      key: 'coverage-drop',
      label: 'Coverage Drop Percentage',
      description: 'Alert if coverage falls by the configured percentage in the defined window.',
      value: 15,
      enabled: false,
      unit: '%',
      min: 0,
      max: 100,
      step: 1,
    },
    {
      key: 'lookback-days',
      label: 'Lookback Days',
      description: 'Number of days to look back when calculating alert metrics.',
      value: 7,
      enabled: true,
      unit: 'days',
      min: 1,
      max: 30,
      step: 1,
    },
  ]);

  const togglePreferenceExpansion = (key) => {
    setExpandedPreference((prev) => (prev === key ? null : key));
  };

  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [emails, setEmails] = useState(['alerts@example.com', 'support@example.com']);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const toggleAlertPreferences = () => {
    setAlertsEnabled((prev) => !prev);
    setAlertPreferences((prev) =>
      prev.map((item) => ({
        ...item,
        enabled: !alertsEnabled,
      }))
    );
  };

  const validateEmail = (value) => {
    if (!value.trim()) {
      return 'Email is required.';
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(value.trim())) {
      return 'Enter a valid email address.';
    }
    if (emails.includes(value.trim().toLowerCase())) {
      return 'Email already added.';
    }
    return '';
  };

  const handleAddEmail = () => {
    const validationMessage = validateEmail(newEmail);
    if (validationMessage) {
      setEmailError(validationMessage);
      return;
    }

    setEmails((prev) => [...prev, newEmail.trim().toLowerCase()]);
      setNewEmail('');
    setEmailError('');
  };

  const handleRemoveEmail = (emailToRemove) => {
    setEmails((prev) => prev.filter((email) => email !== emailToRemove));
  };

  const updatePreferenceValue = (key, rawValue) => {
    setAlertPreferences((prev) =>
      prev.map((item) => {
        if (item.key !== key) {
          return item;
        }

        const parsed = Number(rawValue);
        if (Number.isNaN(parsed)) {
          return item;
        }

        const clamped = Math.min(Math.max(parsed, item.min ?? parsed), item.max ?? parsed);
        return { ...item, value: clamped };
      })
    );
  };

  return (
    <div className="settings-container">
      <div className="settings-sidebar">
        <div className="settings-sidebar-header">
          <h2 className="settings-title">Settings</h2>
        </div>
        <nav className="settings-nav">
          <button
            className={`settings-nav-item ${activeTab === 'alert-preferences' ? 'active' : ''}`}
            onClick={() => setActiveTab('alert-preferences')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 8A6 6 0 0 0 6 8C6 11 3 13 3 16H21C21 13 18 11 18 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Alert Preferences</span>
          </button>
          <button
            className={`settings-nav-item ${activeTab === 'alert-emails' ? 'active' : ''}`}
            onClick={() => setActiveTab('alert-emails')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Alert Emails</span>
          </button>
          <button
            className={`settings-nav-item ${activeTab === 'meta-integration' ? 'active' : ''}`}
            onClick={() => setActiveTab('meta-integration')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Meta Integration</span>
          </button>
        </nav>
      </div>

      <div className="settings-content">
        <div className="settings-content-header">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="settings-back-button"
            title="Back to Dashboard"
            aria-label="Back to Dashboard"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="settings-panel">
          {activeTab === 'alert-preferences' && (
            <div className="settings-section">
              <div className="alert-preferences-header">
                <h3 className="settings-section-title">Alert Preferences</h3>
                <label className={`alert-master-toggle ${alertsEnabled ? 'enabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={alertsEnabled}
                    onChange={toggleAlertPreferences}
                    aria-label={alertsEnabled ? 'Disable alert preferences' : 'Enable alert preferences'}
                  />
                  <span className="toggle-track">
                    <span className="toggle-thumb" />
                  </span>
                </label>
              </div>


              <div className="alert-preferences-list">
                {alertPreferences.map((preference) => {
                  const isExpanded = expandedPreference === preference.key;
                  return (
                    <div
                      key={preference.key}
                      className={`alert-preference-card ${isExpanded ? 'expanded' : ''}`}
                    >
                      <button
                        type="button"
                        className="alert-preference-header"
                        onClick={() => togglePreferenceExpansion(preference.key)}
                        aria-expanded={isExpanded}
                      >
                        <div className="alert-preference-summary">
                          <span className="alert-preference-label">{preference.label}</span>
                          <span className="alert-preference-value">
                            {preference.value}
                            {preference.unit ? ` ${preference.unit}` : ''}
                          </span>
                        </div>
                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} />
                      </button>

                      {isExpanded && (
                        <div className="alert-preference-body">
                          <p className="alert-preference-description">{preference.description}</p>
                          <div className="alert-preference-controls">
                            <div className="alert-preference-control">
                              <label htmlFor={`${preference.key}-value`}>Value</label>
                              <input
                                id={`${preference.key}-value`}
                                type="number"
                                min={preference.min}
                                max={preference.max}
                                step={preference.step}
                                value={preference.value}
                                onChange={(event) => updatePreferenceValue(preference.key, event.target.value)}
                                disabled={!alertsEnabled}
                              />
                            </div>
                          </div>
                          {!alertsEnabled && (
                            <div className="alert-preference-disabled-note">
                              Alerts are turned off. Enable alerts to edit and activate these settings.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
                </div>
              )}

          {activeTab === 'alert-emails' && (
                <div className="settings-section">
              <h3 className="settings-section-title">Alert Email Recipients</h3>
                  

              <div className="alert-emails-form">
                <div className="alert-email-input-group">
                  <label htmlFor="alert-email-input">Add Email</label>
                  <div className="alert-email-input-row">
                  <input
                      id="alert-email-input"
                    type="email"
                      placeholder="name@example.com"
                    value={newEmail}
                      onChange={(event) => {
                        setNewEmail(event.target.value);
                        setEmailError('');
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleAddEmail();
                        }
                      }}
                    />
                    <button type="button" onClick={handleAddEmail} disabled={!newEmail.trim()}>
                      Add
                  </button>
                  </div>
                  {emailError && <p className="alert-email-error">{emailError}</p>}
                </div>

                <div className="alert-email-list">
                  {emails.length ? (
                    emails.map((email) => (
                      <div className="alert-email-item" key={email}>
                        <div className="alert-email-meta">
                          <i className="fas fa-envelope" />
                          <span>{email}</span>
                        </div>
                        <button
                          type="button"
                          className="alert-email-remove"
                          onClick={() => handleRemoveEmail(email)}
                          aria-label={`Remove email ${email}`}
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="alert-email-empty">
                      <i className="fas fa-inbox" />
                      <p>No alert recipients yet.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'meta-integration' && (
            <div className="settings-section">
              <div className="coming-soon-container">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3>Coming Soon</h3>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;

